import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { AuditChecklist, AuditScore, scoreColor, CustomCriteria } from "@/components/AuditView";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ClipboardCheck, ArrowUpRight, TrendingUp, PieChart as PieIcon, BarChart3, SlidersHorizontal } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";

const DIST_META = [
  { key: "green", label: "Compliant (≥80%)", color: "#10B981" },
  { key: "amber", label: "Needs work (50–79%)", color: "#F59E0B" },
  { key: "red", label: "At risk (<50%)", color: "#EF4444" },
];

function TeamSummary({ summary, onDrill }) {
  if (!summary || !summary.projects) return null;
  const dist = DIST_META.map((d) => ({ ...d, value: summary.distribution?.[d.key] || 0 })).filter((d) => d.value > 0);
  const stds = [...(summary.standards || [])].sort((a, b) => a.pct - b.pct);
  const trend = (summary.trend || []).map((t) => ({ week: t.week.split("-")[1], score: t.score })).filter((t) => t.score != null);
  const avgColor = scoreColor(summary.team_avg);
  return (
    <div className="grid lg:grid-cols-3 gap-4 mb-8" data-testid="audit-team-summary">
      <div className="border border-border rounded-lg p-5" data-testid="audit-team-distribution">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-1"><PieIcon className="h-3.5 w-3.5" /> Team compliance</p>
        <div className="flex items-center gap-4">
          <div className="relative h-36 w-36 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={dist.length ? dist : [{ value: 1, color: "#E2E8F0" }]} dataKey="value" innerRadius={44} outerRadius={62} paddingAngle={dist.length > 1 ? 3 : 0} strokeWidth={0}>
                  {(dist.length ? dist : [{ color: "#E2E8F0" }]).map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="font-display text-2xl font-extrabold" style={{ color: avgColor }}>{summary.team_avg}%</span>
              <span className="text-[10px] text-muted-foreground">team avg</span>
            </div>
          </div>
          <div className="space-y-2 min-w-0 flex-1">
            {DIST_META.map((d) => (
              <button key={d.key} type="button" onClick={() => onDrill({ title: d.label, tint: d.color, projects: summary.distribution_projects?.[d.key] || [] })} className="flex items-center gap-2 text-xs w-full rounded px-1 py-0.5 hover:bg-secondary/50 cursor-pointer" data-testid={`dist-drill-${d.key}`}>
                <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: d.color }} />
                <span className="text-muted-foreground truncate">{d.label}</span>
                <b className="ml-auto tabular-nums">{summary.distribution?.[d.key] || 0}</b>
              </button>
            ))}
            <p className="text-[11px] text-muted-foreground pt-1">{summary.projects} project{summary.projects === 1 ? "" : "s"} · {summary.pm_count} PM{summary.pm_count === 1 ? "" : "s"}</p>
          </div>
        </div>
      </div>
      <div className="border border-border rounded-lg p-5" data-testid="audit-standards-compliance">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-3"><BarChart3 className="h-3.5 w-3.5" /> Standards compliance</p>
        <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
          {stds.map((s) => (
            <button key={s.key} type="button" onClick={() => onDrill({ title: `Missing: ${s.label}`, tint: scoreColor(s.pct), projects: s.failing || [] })} className="flex items-center gap-2 w-full rounded px-1 py-0.5 hover:bg-secondary/50 cursor-pointer" data-testid={`std-compliance-${s.key}`}>
              <span className="text-[11px] text-muted-foreground truncate w-40 shrink-0 text-left" title={s.label}>{s.label}</span>
              <div className="h-2 flex-1 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${s.pct}%`, background: scoreColor(s.pct) }} />
              </div>
              <span className="text-[11px] font-bold tabular-nums w-12 text-right" style={{ color: scoreColor(s.pct) }}>{s.met}/{s.total}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="border border-border rounded-lg p-5" data-testid="audit-team-trend">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-1"><TrendingUp className="h-3.5 w-3.5" /> 12-week team trend</p>
        {trend.length > 1 ? (
          <ResponsiveContainer width="100%" height={150}>
            <AreaChart data={trend} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
              <defs>
                <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0044FF" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#0044FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="week" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => [`${v}%`, "Team avg"]} labelFormatter={(l) => `Week ${l}`} />
              <Area type="monotone" dataKey="score" stroke="#0044FF" strokeWidth={2} fill="url(#trendFill)" dot={{ r: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[150px] flex flex-col items-center justify-center text-center">
            <TrendingUp className="h-6 w-6 text-muted-foreground mb-2" />
            <p className="text-xs text-muted-foreground">Trend builds weekly — first snapshots captured this week.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Sparkline({ trend }) {
  const known = (trend || []).map((t, i) => ({ i, score: t.score })).filter((p) => p.score != null);
  if (known.length === 0) return null;
  const W = 132, H = 34, n = (trend || []).length - 1 || 1;
  const x = (i) => (i / n) * (W - 8) + 4;
  const y = (s) => H - 5 - (s / 100) * (H - 10);
  const pts = known.map((p) => `${x(p.i)},${y(p.score)}`).join(" ");
  const last = known[known.length - 1];
  const first = known[0];
  const delta = last.score - first.score;
  const color = known.length < 2 ? "#94A3B8" : delta >= 0 ? "#10B981" : "#EF4444";
  return (
    <div className="flex items-center gap-2 shrink-0" title={`12-week PM score trend (${known.length} week${known.length === 1 ? "" : "s"} of data)`} data-testid="pm-trend-sparkline">
      <svg width={W} height={H} className="overflow-visible">
        <line x1="4" y1={y(50)} x2={W - 4} y2={y(50)} stroke="currentColor" strokeOpacity="0.12" strokeDasharray="3 3" />
        {known.length > 1 && <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
        {known.map((p) => <circle key={p.i} cx={x(p.i)} cy={y(p.score)} r={p.i === last.i ? 3 : 1.5} fill={color} />)}
      </svg>
      {known.length > 1 ? (
        <span className="text-[11px] font-bold tabular-nums" style={{ color }}>{delta >= 0 ? "+" : ""}{delta}</span>
      ) : (
        <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1"><TrendingUp className="h-3 w-3" /> building trend</span>
      )}
    </div>
  );
}

export default function AuditPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [drill, setDrill] = useState(null);
  const [criteriaOpen, setCriteriaOpen] = useState(false);

  const load = () => api.get("/audit/overview").then(({ data }) => setData(data)).catch(() => setData({ pms: [] }));

  useEffect(() => {
    if (!user || !["supervisor", "admin"].includes(user.role)) return;
    load();
  }, [user]);

  if (user && !["supervisor", "admin"].includes(user.role)) return <Navigate to="/dashboard" replace />;

  const openDetail = async (proj, pmName) => {
    try {
      const { data } = await api.get(`/projects/${proj.id}/audit`);
      setDetail({ ...data, pm_name: pmName });
    } catch (e) { toast.error("Could not load audit detail"); }
  };

  return (
    <Layout>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-muted-foreground mb-2">Supervisor view</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center gap-3"><ClipboardCheck className="h-9 w-9 text-primary" /> Audit</h1>
          <p className="text-muted-foreground mt-2">Project standards compliance and PM scores{data?.scope === "assigned" ? " for your assigned PMs" : " across all PMs"}.</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => setCriteriaOpen(true)} data-testid="audit-criteria-button">
          <SlidersHorizontal className="h-4 w-4" /> Manage criteria
        </Button>
      </div>

      {!data ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : data.pms.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="audit-empty">
          <p className="text-muted-foreground">No PMs with projects to audit yet.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="audit-overview">
          <TeamSummary summary={data.summary} onDrill={setDrill} />
          {data.pms.map((pm) => (
            <div key={pm.id} className="border border-border rounded-lg overflow-hidden" data-testid={`audit-pm-${pm.id}`}>
              <div className="flex items-center gap-4 px-5 py-4 bg-secondary/30 border-b border-border">
                <div className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0" style={{ background: `${scoreColor(pm.avg_score)}1a`, color: scoreColor(pm.avg_score), border: `2px solid ${scoreColor(pm.avg_score)}` }}>
                  {pm.avg_score}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold">{pm.name} <span className="text-xs font-normal text-muted-foreground">· {pm.email}</span></p>
                  <p className="text-xs text-muted-foreground">{pm.projects.length} project{pm.projects.length === 1 ? "" : "s"} · avg PM score <b style={{ color: scoreColor(pm.avg_score) }}>{pm.avg_score}%</b></p>
                </div>
                <Sparkline trend={pm.trend} />
              </div>
              <div className="divide-y divide-border">
                {pm.projects.map((p) => (
                  <div key={p.id} className="flex items-center gap-4 px-5 py-3 hover:bg-secondary/20" data-testid={`audit-project-${p.id}`}>
                    <span className="text-sm font-medium min-w-0 flex-1 truncate">{p.name}</span>
                    <div className="flex items-center gap-2 w-40">
                      <div className="h-1.5 flex-1 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${p.score}%`, background: scoreColor(p.score) }} /></div>
                      <span className="text-xs font-bold w-10 text-right" style={{ color: scoreColor(p.score) }}>{p.score}%</span>
                    </div>
                    <span className="text-xs text-muted-foreground w-14 text-right">{p.met}/{p.total}</span>
                    <button onClick={() => openDetail(p, pm.name)} className="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1 shrink-0" data-testid={`audit-detail-${p.id}`}>
                      Checklist <ArrowUpRight className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={criteriaOpen} onOpenChange={setCriteriaOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto grid-cols-[minmax(0,1fr)]" data-testid="audit-criteria-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><SlidersHorizontal className="h-5 w-5 text-primary" /> Audit criteria</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Criteria you add apply to every project's audit across the portfolio — scores refresh automatically.</p>
          <CustomCriteria onChanged={load} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="audit-drill-dialog">
          {drill && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl flex items-center gap-2">
                  <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: drill.tint }} /> {drill.title}
                  <span className="text-sm font-normal text-muted-foreground">({drill.projects.length})</span>
                </DialogTitle>
              </DialogHeader>
              {drill.projects.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8" data-testid="audit-drill-empty">No projects here — all clear.</p>
              ) : (
                <div className="divide-y divide-border border border-border rounded-lg overflow-hidden">
                  {drill.projects.map((p) => (
                    <button key={p.id} onClick={() => { setDrill(null); nav(`/projects/${p.id}?tab=audit`); }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/30 text-left group" data-testid={`audit-drill-project-${p.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{p.owner}{p.detail ? ` · ${p.detail}` : ""}</p>
                      </div>
                      {p.score != null && <span className="text-xs font-bold shrink-0" style={{ color: scoreColor(p.score) }}>{p.score}%</span>}
                      <ArrowUpRight className="h-3.5 w-3.5 shrink-0 opacity-0 group-hover:opacity-70" />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-primary" /> {detail.project_name}</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-muted-foreground -mt-2">PM: {detail.pm_name}</p>
              <AuditScore audit={detail} />
              <AuditChecklist audit={detail} readOnly />
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
